import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import BrandEditorSidebar from '../components/brands/BrandEditorSidebar';
import ConfirmationModal from '../components/ConfirmationModal';

const BrandManagement = () => {
    const { brands, addBrand, updateBrand, deleteBrand } = useApp();
    const [searchQuery, setSearchQuery] = useState('');
    const [showSidebar, setShowSidebar] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, brand: null });

    const filteredBrands = brands.filter((brand) =>
        brand.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        brand.industry.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleEdit = (brand) => {
        setEditingBrand(brand);
        setShowSidebar(true);
    };

    const handleCreate = () => {
        setEditingBrand(null);
        setShowSidebar(true);
    };

    const handleCloseSidebar = () => {
        setShowSidebar(false);
        setEditingBrand(null);
    };

    const handleSave = async (id, brandData, isUpdate) => {
        if (isUpdate) {
            await updateBrand(id, brandData);
        } else {
            await addBrand(brandData);
        }
    };

    const handleDeleteClick = (brand) => {
        setDeleteConfirm({ isOpen: true, brand });
    };

    const handleConfirmDelete = async () => {
        if (deleteConfirm.brand) {
            await deleteBrand(deleteConfirm.brand.id);
        }
    };

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h1>Brand Management</h1>
                    <p className="text-secondary">Create and manage your brand identities</p>
                </div>
                <button className="btn btn-primary" onClick={handleCreate}>
                    <Plus size={20} /> New Brand
                </button>
            </div>

            <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
                <div className="input-group">
                    <Search size={20} className="text-secondary" />
                    <input
                        type="text"
                        className="input-field"
                        placeholder="Search brands..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ paddingLeft: 'var(--space-10)' }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
                {filteredBrands.map((brand) => (
                    <div key={brand.id} className="card hover-lift">
                        <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                            <div
                                style={{
                                    width: '80px',
                                    height: '80px',
                                    background: `linear-gradient(135deg, ${brand.visual.primaryColor}, ${brand.visual.secondaryColor})`,
                                    borderRadius: 'var(--radius-lg)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '2rem',
                                    flexShrink: 0,
                                    overflow: 'hidden',
                                    padding: 'var(--space-2)'
                                }}
                            >
                                {brand.logo && (brand.logo.startsWith('http') || brand.logo.startsWith('data:')) ? (
                                    <img src={brand.logo} alt={brand.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                ) : (
                                    brand.logo
                                )}
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3 style={{ marginBottom: 'var(--space-1)' }}>{brand.name}</h3>
                                <span className="badge badge-neutral">{brand.industry}</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)' }}>
                            {Object.entries(brand.visual).map(([key, color]) => (
                                <div
                                    key={key}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: 'var(--radius-md)',
                                        background: color,
                                        border: '2px solid var(--color-border)',
                                    }}
                                    title={`${key}: ${color}`}
                                />
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => handleEdit(brand)}>
                                <Edit size={16} /> Edit
                            </button>
                            <button className="btn btn-danger" onClick={() => handleDeleteClick(brand)}>
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {filteredBrands.length === 0 && (
                <div className="empty-state">
                    <p>No brands found. Create your first brand to get started!</p>
                </div>
            )}

            <BrandEditorSidebar
                isOpen={showSidebar}
                onClose={handleCloseSidebar}
                onSave={handleSave}
                editingBrand={editingBrand}
            />

            <ConfirmationModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, brand: null })}
                onConfirm={handleConfirmDelete}
                title="Delete Brand?"
                message={`Are you sure you want to delete "${deleteConfirm.brand?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />
        </div>
    );
};

export default BrandManagement;
